/**
 * ฝ่ามือมังกรทองเก้าสวรรค์: an ORIGINAL palm art of golden dragons, from the nine-swords stone (lane C0-VFX; moved here
 * from the apps/c0-vfx clone by C0-VFX-PREP, content lives in shared). Nine forms: the basic chain (its finisher looses a
 * small dragon, drawn by the client) and eight moves on keys 1–8. Copyright first: no names, form names, counts or
 * wording from any existing work; the names are our own.
 *
 * Data only: added to `SKILLS` and `MARTIAL_ARTS` in data/skills.ts like data/arts.ts. Not in the starting kit; how a
 * player earns it (a teacher, a quest) is still open.
 */
import type { MartialArtDef, SkillDef } from './skills';

export const DRAGON_PALM_ART = 'golden-dragon-palm';

export const DRAGON_PALM_SKILLS: readonly SkillDef[] = [
  {
    id: 'dp9-burst',
    name: 'ฝ่ามือมังกรตื่น',
    glyph: '掌',
    kind: 'attack',
    qi: 8,
    cooldown: 2.4,
    windup: 0,
    range: 2,
    power: 1.8,
    fx: 'palm',
    desc: 'ผลักฝ่ามือระยะประชิด มังกรทองพุ่งออกจากฝ่ามือกระแทกศัตรู',
  },
  {
    id: 'dp9-lance',
    name: 'มังกรทองพุ่งทะลวง',
    glyph: '衝',
    kind: 'attack',
    qi: 16,
    cooldown: 5,
    windup: 0,
    range: 5,
    power: 1.6,
    fx: 'palm',
    desc: 'ส่งมังกรทองหนึ่งตัวพุ่งเป็นเส้นตรงไปกัดศัตรูที่อยู่ไกล 5 ช่อง',
  },
  {
    id: 'dp9-twin',
    name: 'มังกรคู่ฝ่าเมฆ',
    glyph: '雲',
    kind: 'attack',
    qi: 24,
    cooldown: 9,
    windup: 0,
    range: 6,
    ground: true,
    land: 0.55,
    aoe: 2.4,
    power: 0.8,
    fx: 'palm',
    desc: 'ปล่อยมังกรทองตัวเล็กสามตัวโค้งผ่านเมฆไปหาศัตรูในระยะ 6 ช่อง ทีละตัว',
  },
  {
    id: 'dp9-rend',
    name: 'ฝ่ามือมังกรผ่าศิลา',
    glyph: '破',
    kind: 'break',
    qi: 0,
    cooldown: 1,
    windup: 0.5,
    range: 2,
    power: 1.4,
    stun: 2,
    fx: 'palm',
    desc: 'ท่าทำลาย ชาร์จครึ่งวินาที หัวมังกรทองงับลงจากเบื้องบน หากศัตรูตั้งรับอยู่ การ์ดจะแตกและมึนงง',
  },
  {
    id: 'dp9-stomp',
    name: 'กระทืบปฐพีปลุกมังกร',
    glyph: '踏',
    kind: 'buff',
    qi: 0,
    cooldown: 16,
    windup: 0,
    range: 0,
    power: 0,
    self: { status: 'golden-guard' },
    fx: 'aura',
    desc: 'ตั้งรับรอ 8 วินาที ถ้าตั้งรับท่าได้จะได้คำรามมังกรทอง โจมตีแรงขึ้น 30% และได้ความโกรธ',
  },
  {
    id: 'dp9-whirl',
    name: 'มังกรวนวงทอง',
    glyph: '旋',
    kind: 'attack',
    qi: 22,
    cooldown: 8,
    windup: 0,
    range: 0,
    power: 1.5,
    aoe: 2.4,
    fx: 'palm',
    desc: 'มังกรทองวนรอบตัวหนึ่งรอบ ฟาดศัตรูทุกตัวในรัศมี 2 ช่อง',
  },
  {
    id: 'dp9-double',
    name: 'ฝ่ามือมังกรซ้อนคู่',
    glyph: '震',
    kind: 'attack',
    qi: 14,
    cooldown: 4,
    windup: 0,
    range: 2,
    power: 1.0,
    hits: 2,
    fx: 'palm',
    desc: 'ผลักฝ่ามือสองจังหวะติดกัน มังกรทองพุ่งออกทีละตัว โดนสองครั้ง',
  },
  {
    id: 'dp9-sky',
    name: 'มังกรทองเก้าสวรรค์',
    glyph: '龍',
    kind: 'power',
    qi: 0,
    cooldown: 12,
    // The ground warning (data/legendary.ts `LEGENDARY.warn`): the dragon takes this long to come down, and the cast shows
    // where. Never shorter than 0.6 s against a player.
    windup: 0.65,
    range: 6,
    power: 1.2,
    rage: 10,
    ground: true,
    aoe: 3,
    hitStun: 1,
    fx: 'palm',
    desc: 'ท่าพลัง ใช้ความโกรธ 10 มังกรทองตัวใหญ่ลงมาจากฟ้าเก้าชั้น กระแทกพื้นตรงจุดที่เลือก ศัตรูในรัศมี 3 ช่องมึน 1 วินาที ตั้งรับไม่ได้',
  },
];

export const DRAGON_PALM_ART_DEF: MartialArtDef = {
  id: DRAGON_PALM_ART,
  legendary: true,
  name: 'ฝ่ามือมังกรทองเก้าสวรรค์',
  school: 'ศิลาเก้ากระบี่',
  weapon: 'unarmed',
  element: 'yang',
  damage: 'internal',
  desc: 'ฝ่ามือที่ศิลาเก้ากระบี่ซ่อนไว้ ปลุกมังกรทองจากฟ้าเก้าชั้น ทั้งผลักประชิด ส่งมังกรไปไกล และเรียกมังกรใหญ่ลงมาจากฟ้า',
  skills: DRAGON_PALM_SKILLS.map((s) => s.id),
  basic: { power: 1, range: 1.8, rate: 0.75 },
};
